import { t } from "@lingui/macro";
import { CheckCircle, LinkedinLogo, XLogo, Link as LinkIcon, X } from "@phosphor-icons/react";
import React, { useState } from "react";

import { useCssAnimation } from "../hooks/use-css-animation";
import { trackResumeEvent } from "../utils/gtm";

interface PostDownloadShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SITE_URL = "https://ai.stylion.io";
const REFERRAL_URL = `${SITE_URL}?ref=share`;

const LINKEDIN_POST = `Just built my resume with 11 specialized AI agents — market research, STAR interviews, ATS optimization, and more. Try it free:\n${REFERRAL_URL}`;
const TWEET_TEXT = `Just built my resume with 11 AI agents — market research, ATS scoring, the works. Try it free:`;

export const PostDownloadShareModal: React.FC<PostDownloadShareModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const { shouldRender, isExiting } = useCssAnimation(isOpen, {
    duration: 300,
    exitDuration: 200,
  });

  if (!shouldRender) return null;

  const animationClass = isExiting ? "fade-out" : "fade-in";
  const scaleClass = isExiting ? "scale-out" : "scale-in";

  const handleLinkedIn = () => {
    trackResumeEvent.shared("linkedin");
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(REFERRAL_URL)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleTwitter = () => {
    trackResumeEvent.shared("twitter");
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(TWEET_TEXT)}&url=${encodeURIComponent(REFERRAL_URL)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = async () => {
    trackResumeEvent.shared("copy_link");
    try {
      await navigator.clipboard.writeText(REFERRAL_URL);
    } catch {
      const input = document.createElement("input");
      input.value = REFERRAL_URL;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm ${animationClass}`}
        style={{ animationDuration: "200ms" }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 ${animationClass} ${scaleClass}`}
        style={{ animationDuration: "300ms" }}
      >
        <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-[#0b1a2e] border border-white/10 shadow-2xl">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>

          {/* Gradient glow */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-brand-teal/20 blur-[60px] pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/15 blur-[60px] pointer-events-none" />

          <div className="relative p-6 pt-5">
            {/* Success icon */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-brand-teal/20 flex items-center justify-center">
                  <CheckCircle size={32} weight="fill" className="text-brand-teal" />
                </div>
              </div>
            </div>

            {/* Headline */}
            <h3 className="text-center text-lg font-bold text-white mb-1">
              {t`Resume Downloaded!`}
            </h3>
            <p className="text-center text-xs text-white/50 mb-5">
              {t`Know someone job hunting? Spread the word.`}
            </p>

            {/* Share buttons */}
            <div className="space-y-2.5">
              {/* LinkedIn */}
              <button
                onClick={handleLinkedIn}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0A66C2]/15 border border-[#0A66C2]/30 text-white hover:bg-[#0A66C2]/25 hover:border-[#0A66C2]/50 transition-all group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#0A66C2] flex items-center justify-center shrink-0">
                  <LinkedinLogo size={20} weight="fill" className="text-white" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-semibold">{t`Share on LinkedIn`}</span>
                  <span className="text-[10px] text-white/40">{t`Reach your professional network`}</span>
                </div>
              </button>

              {/* Twitter/X */}
              <button
                onClick={handleTwitter}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <XLogo size={18} weight="bold" className="text-white" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-semibold">{t`Share on X`}</span>
                  <span className="text-[10px] text-white/40">{t`Post about your AI-built resume`}</span>
                </div>
              </button>

              {/* Copy referral link */}
              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="w-9 h-9 rounded-lg bg-brand-teal/20 flex items-center justify-center shrink-0">
                  <LinkIcon size={18} weight="bold" className="text-brand-teal" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-semibold">
                    {copied ? t`Copied!` : t`Copy Referral Link`}
                  </span>
                  <span className="text-[10px] text-white/40">{t`Share anywhere — WhatsApp, Telegram, email`}</span>
                </div>
              </button>
            </div>

            {/* Dismiss */}
            <button
              onClick={onClose}
              className="w-full mt-4 text-center text-xs text-white/30 hover:text-white/60 transition-colors py-1"
            >
              {t`Maybe later`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
